import { baseApi, PersonDocumentType } from '@ibk/shared-config';
import { ChannelEnum } from '@ibk/shared-types';
import { logTermsAcceptance, PLATFORM_TERMS_ACCEPT_ORIGIN_LOGIN } from '@ibk/shared-utils';
import { ACCEPT_PLATFORM_TERMS_KIND } from './acceptPlatformTermsKind';
import { ONBOARDING_CONTACT_TOKEN_HEADER } from './onboardingContactTokenHeader';
import { parseTermsAcceptStatusResponse } from './parseTermsAcceptStatusResponse';
export const onboardingApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        hasAccount: builder.query({
            query: ({ cpf }) => {
                const cleanDocumentNumber = cpf.replace(/\D/g, '');
                return {
                    url: '/v2/onboarding/has-access',
                    method: 'GET',
                    params: {
                        documentNumber: cleanDocumentNumber,
                        documentType: PersonDocumentType.CPF,
                    },
                };
            },
            providesTags: ['User'],
        }),
        hasAccountEmployee: builder.query({
            query: ({ cpf }) => {
                const cleanDocumentNumber = cpf.replace(/\D/g, '');
                return {
                    url: '/v1/party-service/person',
                    method: 'GET',
                    params: {
                        documentNumber: cleanDocumentNumber,
                        documentType: PersonDocumentType.CPF,
                    },
                };
            },
            providesTags: ['User'],
        }),
        createAccountPassword: builder.mutation({
            query: ({ document, password }) => {
                const cleanDocumentNumber = document.replace(/\D/g, '');
                return {
                    url: '/v1/onboarding/credentials',
                    method: 'POST',
                    body: {
                        documentNumber: cleanDocumentNumber,
                        password,
                    },
                };
            },
            invalidatesTags: ['Auth'],
        }),
        updatePerson: builder.mutation({
            query: ({ onboardingContactToken, ...body }) => ({
                url: '/v2/party-service/person/contact',
                method: 'PATCH',
                body,
                headers: {
                    [ONBOARDING_CONTACT_TOKEN_HEADER]: onboardingContactToken,
                },
            }),
            invalidatesTags: ['User'],
        }),
        hasValidToken: builder.mutation({
            query: body => ({
                url: '/v1/onboarding/token-validation',
                method: 'POST',
                body,
            }),
        }),
        sendEmailToken: builder.mutation({
            query: body => {
                const version = body.personId ? 'v1' : 'v2';
                return {
                    url: `/${version}/onboarding/resend-token`,
                    method: 'POST',
                    body: {
                        ...body,
                        channel: ChannelEnum.email,
                    },
                };
            },
        }),
        sendPhoneToken: builder.mutation({
            query: body => {
                const version = body.personId ? 'v1' : 'v2';
                return {
                    url: `/${version}/onboarding/resend-token`,
                    method: 'POST',
                    body: {
                        ...body,
                        channel: ChannelEnum.phone,
                    },
                };
            },
        }),
        sendWhatsappToken: builder.mutation({
            query: body => {
                const version = body.personId ? 'v1' : 'v2';
                return {
                    url: `/${version}/onboarding/resend-token`,
                    method: 'POST',
                    body: {
                        ...body,
                        channel: ChannelEnum.whatsapp,
                    },
                };
            },
        }),
        login: builder.mutation({
            query: ({ documentNumber, password }) => {
                const cleanDocumentNumber = documentNumber.replace(/\D/g, '');
                return {
                    url: '/v1/auth/login',
                    method: 'POST',
                    body: {
                        documentNumber: cleanDocumentNumber,
                        password,
                    },
                };
            },
            invalidatesTags: ['Auth', 'User'],
        }),
        resetPassword: builder.mutation({
            query: ({ documentNumber, password }) => {
                const cleanDocumentNumber = documentNumber.replace(/\D/g, '');
                return {
                    url: '/v1/auth/reset-password',
                    method: 'POST',
                    body: {
                        documentNumber: cleanDocumentNumber,
                        password,
                    },
                };
            },
            invalidatesTags: ['Auth'],
        }),
        saveDevice: builder.mutation({
            query: device => ({
                url: '/v1/onboarding/register-device',
                method: 'POST',
                body: device,
            }),
        }),
        testDevice: builder.query({
            query: () => ({
                url: '/v1/party-service/device',
                method: 'GET',
            }),
        }),
        getLinkedAccounting: builder.query({
            query: ({ id, type }) => {
                const route = type === 'organization' ? 'organization' : 'person';
                return {
                    url: `/v1/party-service/${route}/${id}/link-dominio-contabil`,
                    method: 'GET',
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object' && 'result' in response) {
                    return response;
                }
                return { result: false };
            },
        }),
        getMyOrganizations: builder.query({
            query: ({ personId }) => ({
                url: '/v1/party-service/my-organizations',
                method: 'GET',
                params: { person: personId },
            }),
            providesTags: ['Organization'],
        }),
        getCompanies: builder.query({
            query: () => ({
                url: '/v1/person-accesses',
                method: 'GET',
                params: {
                    'requires-account': false,
                },
            }),
            providesTags: ['PersonAccess'],
        }),
        getPlatformTermsPending: builder.query({
            query: () => ({
                url: '/v1/products/management/platform-terms/latest-term',
                method: 'GET',
            }),
            providesTags: ['ProductManagement'],
        }),
        getTermsAcceptStatus: builder.query({
            query: () => '/v1/products/management/platform-terms/pending',
            transformResponse: parseTermsAcceptStatusResponse,
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                }
                catch (cause) {
                    logTermsAcceptance('api.get_terms_accept_status.error', {
                        description: 'GET /platform-terms/pending falhou',
                    }, 'error', cause);
                }
            },
        }),
        acceptPlatformTerms: builder.mutation({
            async onQueryStarted(arg, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                }
                catch (cause) {
                    logTermsAcceptance('api.accept_platform_terms.error', {
                        kind: arg.kind,
                        description: 'POST /platform-terms/accept falhou',
                    }, 'error', cause);
                }
            },
            query: arg => {
                if (arg.kind === ACCEPT_PLATFORM_TERMS_KIND.ONBOARDING_JWT) {
                    return {
                        url: '/v1/products/management/platform-terms/accept',
                        method: 'POST',
                        body: {},
                        headers: {
                            Authorization: `Bearer ${arg.accessToken}`,
                        },
                    };
                }
                return {
                    url: '/v1/products/management/platform-terms/accept',
                    method: 'POST',
                    body: {
                        termId: arg.termId,
                        termVersionId: arg.termVersionId,
                        origin: PLATFORM_TERMS_ACCEPT_ORIGIN_LOGIN,
                    },
                };
            },
            invalidatesTags: ['ProductManagement'],
        }),
    }),
});
export const { useHasAccountQuery, useLazyHasAccountQuery, useHasAccountEmployeeQuery, useLazyHasAccountEmployeeQuery, useCreateAccountPasswordMutation, useUpdatePersonMutation, useHasValidTokenMutation, useSendEmailTokenMutation, useSendPhoneTokenMutation, useSendWhatsappTokenMutation, useLoginMutation, useResetPasswordMutation, useSaveDeviceMutation, useTestDeviceQuery, useLazyTestDeviceQuery, useGetLinkedAccountingQuery, useLazyGetLinkedAccountingQuery, useGetMyOrganizationsQuery, useLazyGetMyOrganizationsQuery, useGetCompaniesQuery, useLazyGetCompaniesQuery, useGetPlatformTermsPendingQuery, useLazyGetPlatformTermsPendingQuery, useGetTermsAcceptStatusQuery, useLazyGetTermsAcceptStatusQuery, useAcceptPlatformTermsMutation, } = onboardingApi;
export { ACCEPT_PLATFORM_TERMS_KIND } from './acceptPlatformTermsKind';
